import React, { useState, useEffect, useCallback } from 'react';

import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import { getUnreadMessagesCount } from '../../utils/messages';

import { FiHome, FiUser, FiHeart, FiMessageSquare, FiSettings, FiLogOut, FiX, FiFileText, FiSearch } from 'react-icons/fi';

const DashboardSidebar = ({ isOpen, onClose }) => {
  const { logout } = useAuth();
  const { notifications } = useSocket();

  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  const [matchesCount, setMatchesCount] = useState(0);

  // Fetch notifications (messages, likes, views)
  const fetchUnreadMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        const nextUnread = data?.data?.unreadMessages;
        setUnreadCount(getUnreadMessagesCount([{ unread: nextUnread }]));

        // Calculate matches notifications (likes + views + new matches)
        const newLikes = data?.data?.newLikesCount || 0;
        const newViews = data?.data?.newViewsCount || 0;
        const newMatches = data?.data?.newMatches || 0;
        setMatchesCount(newLikes + newViews + newMatches);
      }
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  }, [logout]);

  useEffect(() => {
    fetchUnreadMessages();
  }, [fetchUnreadMessages]);

  // Update unread messages when new notifications arrive (but limit frequency)
  useEffect(() => {
    if (notifications.length > 0) {
      // Add a small delay and check if notifications have actually changed
      const timeoutId = setTimeout(() => {
        fetchUnreadMessages(); // Refresh unread count when new notification arrives
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [notifications, fetchUnreadMessages]);

  // Clear unread badge when on the messages page or when global read event fires
  useEffect(() => {
    const handleMessagesRead = () => setUnreadCount(0);

    if (location.pathname.startsWith('/dashboard/messages')) {
      handleMessagesRead();
    } else {
      // When navigating away, refresh from server so badge matches latest unread state
      fetchUnreadMessages();
    }

    window.addEventListener('messages_read', handleMessagesRead);
    window.addEventListener('dashboard_stats_updated', fetchUnreadMessages);
    return () => {
      window.removeEventListener('messages_read', handleMessagesRead);
      window.removeEventListener('dashboard_stats_updated', fetchUnreadMessages);
    };
  }, [location.pathname, fetchUnreadMessages]);

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <FiHome size={20} /> },
    { name: 'Profiles', path: '/dashboard/profiles', icon: <FiSearch size={20} /> },
    { name: 'Posts', path: '/dashboard/posts', icon: <FiFileText size={20} /> },
    { name: 'My Profile', path: '/dashboard/profile', icon: <FiUser size={20} /> },
    { name: 'Matches', path: '/dashboard/matches', icon: <FiHeart size={20} /> },
    { name: 'Messages', path: '/dashboard/messages', icon: <FiMessageSquare size={20} /> },
    { name: 'Settings', path: '/dashboard/settings', icon: <FiSettings size={20} /> },

  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 bottom-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        lg:top-16 lg:shadow-lg
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col
      `}>
        {/* Close button for mobile */}
        <div className="flex justify-end p-2 lg:hidden">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={24} />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `dashboard-sidebar-link flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${isActive
                    ? 'dashboard-sidebar-link-active bg-gradient-to-r from-romantic-50 to-primary-50 text-romantic-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
                onClick={onClose}
              >
                <span className="mr-3">{item.icon}</span>
                <span>{item.name}</span>
                {item.name === 'Matches' && matchesCount > 0 && (
                  <span className="ml-auto bg-romantic-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {matchesCount > 9 ? '9+' : matchesCount}
                  </span>
                )}
                {item.name === 'Messages' && unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}

            <button
              onClick={logout}
              className="dashboard-sidebar-link w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 mt-4"
            >
              <FiLogOut size={20} className="mr-3" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;